import type { CategorisedResponse, FullActivityItem } from '@customTypes/types';
import { ResponsCategory, WorkItemStatus, WorkItemTypes } from '@customTypes/types';
import { DndContext, type DragEndEvent, type DragStartEvent, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Flex } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { errorNotificationStyleConfig } from '@/features/notification/config/errorNotificationConfig';
import { useAdminUpdateWorkItemMutation } from '@/shared/api/requests/work/admin-update-work-item';

import { BoardCol } from '../board-col/BoardCol';
import { TaskCard } from '../task-card/TaskCard';

// Map ResponsCategory to WorkItemStatus
const categoryToStatusMap: Record<ResponsCategory, WorkItemStatus> = {
  [ResponsCategory.pending]: WorkItemStatus.Pending,
  [ResponsCategory.accepted]: WorkItemStatus.Accepted,
  [ResponsCategory.contractOngoing]: WorkItemStatus.ContractOngoing,
  [ResponsCategory.clubSetEndDate]: WorkItemStatus.ClubSetEndDate,
  [ResponsCategory.artistRequestedEndDate]: WorkItemStatus.ContractRequestedEndDate,
  [ResponsCategory.completed]: WorkItemStatus.ContractCompleted,
  [ResponsCategory.notSuccessful]: WorkItemStatus.Cancelled
};

// Russian labels for ResponsCategory
const ResponsCategoryLabelsRu: Record<ResponsCategory, string> = {
  [ResponsCategory.pending]: 'В ожидании',
  [ResponsCategory.accepted]: 'Принято',
  [ResponsCategory.contractOngoing]: 'Контракт в процессе',
  [ResponsCategory.clubSetEndDate]: 'Клуб указал дату',
  [ResponsCategory.artistRequestedEndDate]: 'Артист попросил дату',
  [ResponsCategory.completed]: 'Завершено',
  [ResponsCategory.notSuccessful]: 'Неудачно'
};

// Map Russian column titles back to ResponsCategory
const titleToCategoryMap: Record<string, ResponsCategory> = {
  [ResponsCategoryLabelsRu[ResponsCategory.pending]]: ResponsCategory.pending,
  [ResponsCategoryLabelsRu[ResponsCategory.accepted]]: ResponsCategory.accepted,
  [ResponsCategoryLabelsRu[ResponsCategory.contractOngoing]]: ResponsCategory.contractOngoing,
  [ResponsCategoryLabelsRu[ResponsCategory.clubSetEndDate]]: ResponsCategory.clubSetEndDate,
  [ResponsCategoryLabelsRu[ResponsCategory.artistRequestedEndDate]]: ResponsCategory.artistRequestedEndDate,
  [ResponsCategoryLabelsRu[ResponsCategory.completed]]: ResponsCategory.completed,
  [ResponsCategoryLabelsRu[ResponsCategory.notSuccessful]]: ResponsCategory.notSuccessful
};

export const Board = ({
  data,
  onSelectItem
}: {
  data: CategorisedResponse;
  onSelectItem: (activityItem: FullActivityItem) => void;
}) => {
  const [activeTask, setActiveTask] = useState<FullActivityItem | null>(null);
  const queryClient = useQueryClient();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const updateWorkItemMutation = useAdminUpdateWorkItemMutation({
    options: {
      onMutate: async () => {
        // Cancel any outgoing refetches to avoid overwriting our optimistic update
        await queryClient.cancelQueries({ queryKey: ['GetCategorisedActivityesList'] });
      },
      onSuccess: async () => {
        // Don't show notification immediately - it's too fast and annoying
        // The refetch will happen and update the UI with server data
        await queryClient.invalidateQueries({ queryKey: ['GetCategorisedActivityesList'] });
      },
      onError: (error) => {
        notifications.show({
          ...errorNotificationStyleConfig,
          title: 'Ошибка при обновлении статуса',
          message: error.response?.data?.Detail || 'Попробуйте позже'
        });
        // Refetch to restore original state from server
        queryClient.invalidateQueries({ queryKey: ['GetCategorisedActivityesList'] });
      }
    }
  });

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const task = active.data.current?.task as FullActivityItem;
    setActiveTask(task);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveTask(null);
      return;
    }

    const task = active.data.current?.task as FullActivityItem;
    const sourceColumn = active.data.current?.columnTitle as string;
    const targetColumn = over.id as string;

    // Don't do anything if dropped in the same column
    if (sourceColumn === targetColumn) {
      setActiveTask(null);
      return;
    }

    // Map the target column title to ResponsCategory
    const targetCategory = titleToCategoryMap[targetColumn];
    if (!targetCategory) {
      setActiveTask(null);
      notifications.show({
        ...errorNotificationStyleConfig,
        title: 'Ошибка',
        message: 'Неизвестная колонка'
      });
      return;
    }

    // Get the new status
    const newStatus = categoryToStatusMap[targetCategory];

    // Get the source and target category enum values (these are the actual keys in the API response)
    const sourceCategory = titleToCategoryMap[sourceColumn];

    // Optimistically update the local cache immediately BEFORE clearing drag state
    queryClient.setQueryData<{ data: CategorisedResponse }>(['GetCategorisedActivityesList'], (oldData) => {
      if (!oldData?.data) {
        return oldData;
      }

      if (!sourceCategory || !targetCategory) {
        return oldData;
      }

      // Create a deep copy of the data structure
      const updatedCategories = { ...oldData.data };

      // Create copies of the arrays - the keys are ResponsCategory enum values like 'pending', 'accepted', etc.
      const sourceArray = [...(updatedCategories[sourceCategory] || [])] as FullActivityItem[];
      const targetArray = [...(updatedCategories[targetCategory] || [])] as FullActivityItem[];

      // Find the task index in source
      const taskIndex = sourceArray.findIndex(
        (item) => item.workItemId === task.workItemId && item.workItemType === task.workItemType
      );

      if (taskIndex !== -1) {
        // Remove from source
        const [movedTask] = sourceArray.splice(taskIndex, 1);

        // Update the task status
        const updatedTask = { ...movedTask, status: newStatus };

        // Add to target
        targetArray.push(updatedTask);

        // Update the categories in the cache
        updatedCategories[sourceCategory] = sourceArray;
        updatedCategories[targetCategory] = targetArray;
      }

      return { ...oldData, data: updatedCategories };
    });

    // Clear the active task AFTER the optimistic update
    setActiveTask(null);

    // Update the work item on the server
    updateWorkItemMutation.mutate({
      params: {
        ...(task.workItemType === WorkItemTypes.Request
          ? { workRequestId: task.workItemId }
          : { workInviteId: task.workItemId }),
        status: newStatus,
        startDate: task.startDate,
        endDate: task.endDate,
        ticketCreditation: task.ticketCreditation,
        jobRole: task.jobRole,
        arrivalConfirmed: task.arrivalConfirmed
      }
    });
  };

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <Flex gap={20} wrap='nowrap'>
        {data &&
          Object.entries(data).map(([key, value]) => (
            <BoardCol
              key={key}
              colTitle={key}
              data={value}
              renderItem={TaskCard}
              onSelectItem={onSelectItem}
            />
          ))}
      </Flex>
      <DragOverlay dropAnimation={null}>
        {activeTask ? <TaskCard task={activeTask} onClick={() => {}} /> : null}
      </DragOverlay>
    </DndContext>
  );
};
